'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Users, FileSignature, Calendar, MessageSquare, Palette, Star, ChevronRight, Music2, BarChart3, CheckCircle, ArrowRight, Play, Sparkles, Bot, Truck, Loader2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const features = [
  {
    icon: Bot,
    title: 'AI Virtual Assistant',
    desc: '24/7 chatbot handles FAQs, booking inquiries, and vendor support — trained on your brand voice.',
    color: 'var(--neon-cyan)',
    span: 'col-span-2 row-span-1',
  },
  {
    icon: Truck,
    title: 'Automated Vendor Dispatch',
    desc: 'Auto-contact vendors, check availability, and provision login credentials instantly.',
    color: 'var(--neon-purple)',
    span: 'col-span-1 row-span-2',
  },
  {
    icon: FileSignature,
    title: 'E-Signature Contracts',
    desc: 'Auto-generated legal contracts with dynamic retainer fees and cancellation clauses.',
    color: 'var(--neon-gold)',
    span: 'col-span-1 row-span-1',
  },
  {
    icon: Calendar,
    title: 'Live Booking Calendar',
    desc: 'Real-time availability with instant lead capture and pricing preview.',
    color: 'var(--neon-cyan)',
    span: 'col-span-1 row-span-1',
  },
  {
    icon: MessageSquare,
    title: 'Real-Time Messaging',
    desc: 'Per-booking chat threads between clients, vendors, and admins with live updates.',
    color: 'var(--neon-pink)',
    span: 'col-span-1 row-span-1',
  },
  {
    icon: Palette,
    title: 'White-Label Branding',
    desc: 'Your logo, colors, domain, and brand voice — fully customizable in minutes.',
    color: 'var(--neon-gold)',
    span: 'col-span-2 row-span-1',
  },
];

const portals = [
  {
    title: 'Client Portal',
    icon: Users,
    color: 'var(--neon-gold)',
    borderClass: 'neon-border-gold',
    features: [
      'Contract e-signing & payment gateway',
      'Spotify playlist builder',
      'Minute-by-minute timeline builder',
      'Live assigned pros display',
      '14 vendor add-on categories',
      'Real-time invoice updates',
    ],
  },
  {
    title: 'Vendor Portal',
    icon: Truck,
    color: 'var(--neon-cyan)',
    borderClass: 'neon-border-cyan',
    features: [
      'Assigned jobs manager',
      'Payout & financials tracker',
      'Direct messaging hub',
      'Accept/decline job offers',
      'Availability calendar',
      'Auto-provisioned credentials',
    ],
  },
  {
    title: 'Admin Dashboard',
    icon: BarChart3,
    color: 'var(--neon-purple)',
    borderClass: '',
    features: [
      'Live master calendar',
      'Leads kanban board',
      'Vendor CRM directory',
      'Financial & payment tracker',
      'PDF day-of brief exporter',
      'Business branding settings',
    ],
  },
];

const plans = [
  {
    name: 'Starter',
    price: 49,
    desc: 'Perfect for solo DJs getting started',
    features: ['Public website + booking calendar', 'Client portal (e-sign + payments)', 'Up to 5 vendors in CRM', 'AI assistant (100 msgs/mo)', 'Basic branding customization'],
    cta: 'Start Free Trial',
    highlight: false,
  },
  {
    name: 'Professional',
    price: 99,
    desc: 'For growing entertainment businesses',
    features: ['Everything in Starter', 'Vendor portal + auto-dispatch', 'Unlimited vendors in CRM', 'AI assistant (unlimited)', 'Full white-label branding', 'Real-time messaging', 'PDF exporter'],
    cta: 'Start Free Trial',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 199,
    desc: 'For agencies managing multiple brands',
    features: ['Everything in Professional', 'Multi-brand management', 'Custom domain + SSL', 'Priority support', 'API access', 'Custom contract templates', 'Dedicated onboarding'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

const testimonials = [
  {
    name: 'Marcus T.',
    title: 'Owner, Platinum Sound Events',
    city: 'Atlanta, GA',
    quote: 'This platform replaced 4 different tools I was paying for. My clients love the portal and vendors actually show up prepared now.',
    stars: 5,
  },
  {
    name: 'Destiny R.',
    title: 'DJ Destiny Rose Entertainment',
    city: 'Houston, TX',
    quote: 'The automated vendor dispatch alone saved me 10 hours a week. I booked 3 extra weddings last month because I had more time to sell.',
    stars: 5,
  },
  {
    name: 'Kevin M.',
    title: 'CEO, NightLife Pro Events',
    city: 'Chicago, IL',
    quote: 'White-labeling was seamless. My clients think I built this myself. The AI assistant answers inquiries at 2am while I sleep.',
    stars: 5,
  },
];

export default function WhiteLabelLanding() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) setSubmitted(true);
  };

  const handleSubscribe = async (planName: string) => {
    if (planName === 'Enterprise') return;
    setLoadingPlan(planName);
    setCheckoutError(null);
    try {
      const res = await fetch('/api/white-label/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planName, interval: 'monthly' }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setCheckoutError(data.error || 'Unable to start checkout. Please try again.');
        setLoadingPlan(null);
        return;
      }
      window.location.href = data.url;
    } catch {
      setCheckoutError('Network error. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <main className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Ambient backgrounds */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="light-burst w-96 h-96 bg-primary top-[-10%] left-[-5%]" />
        <div className="light-burst w-80 h-80 bg-accent top-[30%] right-[-5%]" />
        <div className="light-burst w-64 h-64" style={{ background: 'var(--neon-purple)', bottom: '20%', left: '5%' }} />
      </div>

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/90 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center">
              <Music2 size={16} className="text-black" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[10px] font-mono text-muted-foreground tracking-widest uppercase">DJ Platform</span>
              <span className="font-bold text-sm gold-gradient-text">EventPro Suite</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#portals" className="hover:text-primary transition-colors">Portals</a>
            <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
            <a href="#testimonials" className="hover:text-primary transition-colors">Reviews</a>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/white-label/demo" className="px-4 py-2 text-sm font-semibold rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition-all">
              Book Demo
            </Link>
            <button
              onClick={() => handleSubscribe('Professional')}
              disabled={loadingPlan === 'Professional'}
              className="px-4 py-2 text-sm font-semibold rounded-lg gold-gradient text-black hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2"
            >
              {loadingPlan === 'Professional' ? <Loader2 size={14} className="animate-spin" /> : 'Start Free Trial'}
            </button>
          </div>
        </div>
      </nav>

      <div className="relative z-10 pt-16">
        {/* HERO */}
        <section className="min-h-screen flex flex-col items-center justify-center px-6 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/30 bg-accent/5 text-accent text-xs font-mono mb-8">
            <Sparkles size={12} />
            WHITE-LABEL PLATFORM FOR DJ & EVENT BUSINESSES
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6 max-w-4xl">
            <span className="gold-gradient-text">The Ultimate</span>
            <br />
            <span className="text-foreground">DJ Business Platform</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
            Run your entire entertainment business from one dashboard. Client portal, vendor dispatch, AI assistant, live calendar, e-signatures, and more — fully white-labeled with your brand.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 mb-16">
            <button
              onClick={() => handleSubscribe('Professional')}
              disabled={loadingPlan === 'Professional'}
              className="px-8 py-4 rounded-xl gold-gradient text-black font-bold text-lg hover:opacity-90 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-60"
            >
              {loadingPlan === 'Professional' ? <Loader2 size={20} className="animate-spin" /> : <>Start Free Trial <ArrowRight size={20} /></>}
            </button>
            <Link href="/white-label/demo" className="px-8 py-4 rounded-xl border border-accent/40 text-accent font-semibold text-lg hover:bg-accent/10 transition-all flex items-center gap-2">
              <Play size={18} /> See Live Demo
            </Link>
          </div>

          {/* Mock portal preview cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl w-full">
            {['Admin Dashboard', 'Client Portal', 'Vendor Portal'].map((label, i) => (
              <div key={label} className="card-glass rounded-2xl p-5 text-left" style={{ border: `1px solid ${i === 0 ? 'rgba(255,215,0,0.3)' : i === 1 ? 'rgba(0,255,255,0.3)' : 'rgba(191,0,255,0.3)'}` }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: i === 0 ? 'var(--neon-gold)' : i === 1 ? 'var(--neon-cyan)' : 'var(--neon-purple)', boxShadow: `0 0 8px ${i === 0 ? 'var(--neon-gold)' : i === 1 ? 'var(--neon-cyan)' : 'var(--neon-purple)'}` }} />
                  <span className="text-xs font-mono text-muted-foreground">{label}</span>
                </div>
                <div className="space-y-2">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-2 rounded-full bg-muted" style={{ width: `${[85, 65, 75, 50][j]}%`, opacity: 0.4 + j * 0.1 }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURES BENTO */}
        <section id="features" className="px-6 py-24 max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold gold-gradient-text mb-4">Everything You Need</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">One platform replaces your booking software, CRM, contract tool, and communication apps.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-auto">
            {features.map((f, i) => {
              const Icon = f.icon;
              const isWide = i === 0 || i === 5;
              const isTall = i === 1;
              return (
                <div
                  key={f.title}
                  className={`card-glass rounded-2xl p-6 flex flex-col gap-4 ${isWide ? 'md:col-span-2' : ''} ${isTall ? 'md:row-span-2' : ''}`}
                  style={{ border: `1px solid ${f.color}30` }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${f.color}15`, border: `1px solid ${f.color}40` }}>
                    <Icon size={22} style={{ color: f.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-foreground mb-2">{f.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                  </div>
                  {isTall && (
                    <div className="mt-auto space-y-2">
                      {['Auto-match vendors by category', 'Send availability requests in 1 click', 'Auto-generate login credentials', 'Track acceptance & payout status'].map(item => (
                        <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle size={12} style={{ color: f.color }} />
                          {item}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* PORTALS SHOWCASE */}
        <section id="portals" className="px-6 py-24 bg-card/30">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold mb-4">
                <span className="gold-gradient-text">Three Portals.</span> One Platform.
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">Separate, secured access for every stakeholder in your business.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portals.map((portal) => {
                const Icon = portal.icon;
                return (
                  <div key={portal.title} className={`card-glass rounded-2xl p-6 ${portal.borderClass}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${portal.color}15` }}>
                        <Icon size={20} style={{ color: portal.color }} />
                      </div>
                      <h3 className="font-bold text-lg" style={{ color: portal.color }}>{portal.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {portal.features.map(f => (
                        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <ChevronRight size={14} className="mt-0.5 flex-shrink-0" style={{ color: portal.color }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="px-6 py-24 max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold gold-gradient-text mb-4">Simple, Transparent Pricing</h2>
            <p className="text-muted-foreground text-lg">No setup fees. Cancel anytime. 14-day free trial on all plans.</p>
          </div>
          {checkoutError && (
            <div className="max-w-xl mx-auto mb-8 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
              {checkoutError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`card-glass rounded-2xl p-8 flex flex-col ${plan.highlight ? 'neon-border-gold glow-gold' : 'border border-border'}`}
              >
                {plan.highlight && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold mb-4 self-start">
                    <Sparkles size={10} /> MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-foreground mb-1">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{plan.desc}</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold gold-gradient-text">${plan.price}</span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.name === 'Enterprise' ? (
                  <Link
                    href="/white-label/demo"
                    className="w-full text-center py-3 rounded-xl font-semibold transition-all border border-primary/40 text-primary hover:bg-primary/10"
                  >
                    Contact Sales
                  </Link>
                ) : (
                  <button
                    onClick={() => handleSubscribe(plan.name)}
                    disabled={loadingPlan === plan.name}
                    className={`w-full text-center py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60 ${plan.highlight ? 'gold-gradient text-black hover:opacity-90' : 'border border-primary/40 text-primary hover:bg-primary/10'}`}
                  >
                    {loadingPlan === plan.name ? (
                      <><Loader2 size={16} className="animate-spin" /> Redirecting...</>
                    ) : plan.cta}
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/white-label/pricing" className="text-sm text-primary hover:underline">
              View full feature comparison →
            </Link>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" className="px-6 py-24 bg-card/30">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold gold-gradient-text mb-4">DJ Owners Love It</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="card-glass rounded-2xl p-6 neon-border-gold">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.stars)].map((_, i) => (
                      <Star key={i} size={14} className="fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                  <div>
                    <p className="font-semibold text-foreground text-sm">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.title}</p>
                    <p className="text-xs text-primary font-mono">{t.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-24 max-w-screen-xl mx-auto text-center">
          <div className="card-glass rounded-3xl p-12 neon-border-gold">
            <h2 className="text-4xl font-extrabold mb-4">
              <span className="gold-gradient-text">Launch Your DJ Business Platform</span>
              <br />
              <span className="text-foreground">Today</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Join hundreds of entertainment professionals running smarter businesses. 14-day free trial, no credit card required.
            </p>
            {submitted ? (
              <div className="flex items-center justify-center gap-2 text-primary font-semibold">
                <CheckCircle size={20} /> You're on the list! We'll be in touch shortly.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  required
                />
                <button type="submit" className="px-6 py-3 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all whitespace-nowrap">
                  Get Early Access
                </button>
              </form>
            )}
          </div>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-border px-6 py-10">
          <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
                <Music2 size={13} className="text-black" />
              </div>
              <span className="font-bold text-sm gold-gradient-text">EventPro Suite</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link href="/white-label/pricing" className="hover:text-primary transition-colors">Pricing</Link>
              <Link href="/white-label/demo" className="hover:text-primary transition-colors">Demo</Link>
              <Link href="/white-label/customize" className="hover:text-primary transition-colors">Customize</Link>
              <Link href="/white-label/admin" className="hover:text-primary transition-colors">Admin</Link>
            </div>
            <p className="text-xs text-muted-foreground">© 2026 EventPro Suite. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </main>
  );
}
